var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import * as React from 'react';
import { useState } from 'react';
import { styled, css, ThemeProvider, } from '../theme-provider';
import { Close } from '@volkswagen-onehub/icons-core';
import { Text, TextTag, TokenTextAppearance, TokenTextColor } from '../text';
const setTooltipPosition = ({ position, theme, }) => {
    const tooltipStyles = {};
    const arrowStyles = {};
    const arrowOffset = `${parseInt(theme.size.static200, 10) - parseInt(theme.size.static100, 10)}px`;
    const [primaryPosition, secondaryPosition] = position.split('-');
    switch (primaryPosition) {
        case 'top':
            tooltipStyles.bottom = `calc(100% + ${theme.size.static200})`;
            arrowStyles['border-top-color'] = theme.colors.content.primary;
            arrowStyles.top = '100%';
            break;
        case 'right':
            tooltipStyles.right = `-${theme.size.static200}`;
            tooltipStyles.transform = 'translateX(100%)';
            arrowStyles['border-right-color'] = theme.colors.content.primary;
            arrowStyles.right = '100%';
            break;
        case 'bottom':
            tooltipStyles.top = `calc(100% + ${theme.size.static200})`;
            arrowStyles['border-bottom-color'] = theme.colors.content.primary;
            arrowStyles.bottom = '100%';
            break;
        case 'left':
            tooltipStyles.left = `-${theme.size.static200}`;
            tooltipStyles.transform = 'translateX(-100%)';
            arrowStyles['border-left-color'] = theme.colors.content.primary;
            arrowStyles.left = '100%';
            break;
    }
    if (primaryPosition === 'top' || primaryPosition === 'bottom') {
        switch (secondaryPosition) {
            case 'start':
                tooltipStyles['inset-inline-start'] = '0';
                arrowStyles['inset-inline-start'] = arrowOffset;
                break;
            case 'end':
                tooltipStyles['inset-inline-end'] = '0';
                arrowStyles['inset-inline-end'] = arrowOffset;
                break;
            default:
                tooltipStyles.left = '50%';
                tooltipStyles.transform = [tooltipStyles.transform, 'translateX(-50%)']
                    .filter(Boolean)
                    .join(' ');
                arrowStyles.left = `calc(50% - ${theme.size.static100})`;
        }
    }
    if (primaryPosition === 'left' || primaryPosition === 'right') {
        switch (secondaryPosition) {
            case 'start':
                tooltipStyles.top = '0';
                arrowStyles.top = arrowOffset;
                break;
            case 'end':
                tooltipStyles.bottom = '0';
                arrowStyles.bottom = arrowOffset;
                break;
            default:
                tooltipStyles.top = '50%';
                tooltipStyles.transform = [tooltipStyles.transform, 'translateY(-50%)']
                    .filter(Boolean)
                    .join(' ');
                arrowStyles.top = `calc(50% - ${theme.size.static100})`;
        }
    }
    return css `${tooltipStyles}::after{${arrowStyles}}`;
};
const StyledTooltip = styled.div.withConfig({ displayName: "StyledTooltip", componentId: "sc-1vsugjj" }) `display:${({ isOpen }) => (isOpen ? 'flex' : 'none')};align-items:center;gap:${({ theme }) => theme.size.static100};position:absolute;padding:${({ theme }) => `${theme.size.static150} ${theme.size.static200}`};border-radius:${({ theme }) => theme.size.static100};justify-content:center;width:max-content;max-width:${({ theme }) => theme.size.static1200};background-color:${({ theme }) => theme.colors.signal.contrast};color:${({ theme }) => theme.colors.content.inverted};text-align:start;z-index:1;${({ reducedFontSize }) => !reducedFontSize &&
    css `
			& span {
				white-space: nowrap;
				text-overflow: ellipsis;
				overflow: hidden;
			}
		`} ${({ hideArrow, theme }) => !hideArrow &&
    css `
			::after {
				content: '';
				position: absolute;
				width: 0;
				height: 0;
				border: ${theme.size.static100} solid transparent;
				display: block;
			}
		`} ${setTooltipPosition}`;
const TooltipWrapper = styled.div.withConfig({ displayName: "TooltipWrapper", componentId: "sc-e84iiq" }) `position:relative;display:inline-block;`;
const ContentWrapper = styled.div.withConfig({ displayName: "ContentWrapper", componentId: "sc-1bpu3pg" }) `cursor:${({ controlled }) => (controlled ? 'pointer' : 'inherit')};${({ controlled }) => !controlled &&
    css `
			:hover + ${StyledTooltip}, :focus + ${StyledTooltip} {
				display: flex;
			}
		`}`;
const CloseWrapper = styled.button.withConfig({ displayName: "CloseWrapper", componentId: "sc-i8ahd3" }) `border:0;padding:0;background:none;color:inherit;cursor:pointer;`;
export const Tooltip = (_a) => {
    var { position = 'top', hideArrow = false, reducedFontSize = false, text, icon, children, isOpen: isOpenProp, onClose, onOpen, uniqueAriaId, theme, closeBtnAriaLabel } = _a, rest = __rest(_a, ["position", "hideArrow", "reducedFontSize", "text", "icon", "children", "isOpen", "onClose", "onOpen", "uniqueAriaId", "theme", "closeBtnAriaLabel"]);
    const [isOpen, setIsOpen] = useState(isOpenProp !== null && isOpenProp !== void 0 ? isOpenProp : false);
    const controlled = Boolean(onClose && onOpen);
    React.useEffect(() => {
        var _a;
        if ((_a = isOpen !== isOpenProp) !== null && _a !== void 0 ? _a : false) {
            setIsOpen(isOpenProp !== null && isOpenProp !== void 0 ? isOpenProp : false);
        }
    }, [isOpenProp, isOpen]);
    const handleOpen = () => {
        controlled && setIsOpen(true);
        onOpen && onOpen();
    };
    const handleClose = () => {
        setIsOpen(false);
        onClose && onClose();
    };
    const handleKeyDownContent = (e) => {
        if (e.key === ' ') {
            controlled && setIsOpen(true);
            onOpen && onOpen();
        }
        else if (e.key === 'Escape') {
            setIsOpen(false);
            onClose && onClose();
        }
    };
    const handleKeyDownClose = (e) => {
        if (e.key === ' ' || e.key === 'Escape' || e.key === 'Enter') {
            setIsOpen(false);
            onClose && onClose();
        }
    };
    return (React.createElement(TooltipWrapper, null,
        React.createElement(ContentWrapper, { controlled: controlled, tabIndex: 0, onClick: handleOpen, onKeyDown: handleKeyDownContent, "aria-describedby": uniqueAriaId }, children),
        React.createElement(ThemeProvider, { theme: theme },
            React.createElement(StyledTooltip, Object.assign({ position: position, hideArrow: hideArrow, isOpen: isOpen, role: controlled ? undefined : 'tooltip', reducedFontSize: reducedFontSize }, rest),
                icon ? icon : undefined,
                React.createElement(Text
                /*
                 id mapped to aria-describedby placed on Text ensures that screen readers only read out the actual content
                 and not the close button aria-label below as well */
                , { 
                    /*
                     id mapped to aria-describedby placed on Text ensures that screen readers only read out the actual content
                     and not the close button aria-label below as well */
                    id: uniqueAriaId, bold: !reducedFontSize, color: TokenTextColor.inherit, appearance: TokenTextAppearance[reducedFontSize ? 'label150' : 'label200'], tag: TextTag.span }, text),
                controlled && isOpen && (React.createElement(CloseWrapper, { onClick: handleClose, onKeyDown: handleKeyDownClose, "aria-label": closeBtnAriaLabel ? closeBtnAriaLabel : 'close tooltip' },
                    React.createElement(Close, { variant: "small", ariaHidden: true })))))));
};
